import axios from 'axios'
import { useAuthStore } from '../stores/auth'
import i18n from '../i18n'
import { isAuthRouteName } from '../router/authRoutes'

const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// eslint-disable-next-line unicorn/no-top-level-side-effects -- configuring the shared axios client is this module's purpose
apiClient.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`
  }
  return config
})

// Module-level in-flight flag so concurrent 401s collapse into a single
// logout + a single redirect to Login. Reset on the next successful response.
let sessionExpiredHandled = false

// Singleton in-flight refresh promise. Concurrent 401s share one refresh
// request instead of stampeding the backend; the next 401 cycle starts fresh
// once this resolves (success or failure).
let refreshPromise = null

// Shared helper called from both the response interceptor (mid-session 401s)
// and the router guard (boot-time fetchUser failures). The in-flight flag
// prevents the logout call from running more than once across concurrent
// callers, but the target is always recomputed from the caller's redirectPath
// so each caller gets accurate info even when it didn't win the race.
// Returns `{ target, wasFirst }`. Callers use `wasFirst` to decide whether
// they should drive the actual navigation.
export async function handleSessionExpired(redirectPath) {
  const wasFirst = !sessionExpiredHandled
  if (wasFirst) {
    sessionExpiredHandled = true
    const authStore = useAuthStore()
    if (authStore.token) {
      await authStore.logout({ localOnly: true })
    }
  }

  const locale = i18n.global.locale.value
  const loginRouteName = locale === 'en' ? 'Login.en' : 'Login'
  const query = { sessionExpired: 'true' }
  if (redirectPath) query.redirect = redirectPath
  return { target: { name: loginRouteName, query }, wasFirst }
}

// eslint-disable-next-line unicorn/no-top-level-side-effects -- configuring the shared axios client is this module's purpose
apiClient.interceptors.response.use(
  (response) => {
    // Any successful response means we have a working session — clear the
    // in-flight flag so a future expiry can trigger the handler again.
    sessionExpiredHandled = false
    return response
  },
  async (error) => {
    const status = error.response?.status
    const url = error.config?.url || ''
    // 401s from /auth/login are wrong-credentials (Login.vue handles inline).
    // 401s from /auth/logout would otherwise recurse, since the auth store
    // calls DELETE /auth/logout while the local token is still set.
    // /auth/refresh goes through bare axios, but guard against an unintended
    // future caller routing it through apiClient.
    const isAuthAttempt =
      url.endsWith('/auth/login') || url.endsWith('/auth/logout') || url.endsWith('/auth/refresh')

    // Only react to 401s for authenticated users — an unauthenticated user
    // hitting a 401 (e.g. clicking something auth-required from a public page)
    // didn't actually have a session that expired, so don't pretend otherwise.
    const authStore = useAuthStore()
    if (status === 401 && !isAuthAttempt && authStore.token) {
      if (authStore.refreshToken && !error.config._retried) {
        try {
          if (!refreshPromise) {
            // eslint-disable-next-line unicorn/prefer-await -- deliberately caching the in-flight refresh promise so concurrent 401s share it, not awaiting it here
            refreshPromise = authStore.refresh().finally(() => {
              refreshPromise = null
            })
          }
          await refreshPromise
          error.config._retried = true
          error.config.headers ||= {}
          error.config.headers.Authorization = `Bearer ${authStore.token}`
          return apiClient(error.config)
        } catch {
          // Refresh failed — fall through to the existing session-expired path.
        }
      }

      // Lazy import to avoid the static cycle: client → router → views → store → client.
      const { default: router } = await import('../router')
      const currentRoute = router.currentRoute.value
      const routeName = currentRoute.name?.toString() || ''
      // currentRoute is START_LOCATION (no resolved name) when the 401 fires
      // inside the router's boot-time beforeEach guard awaiting fetchUser().
      // In that case, calling router.push from here would conflict with the
      // in-progress navigation; the guard's catch block already handles the
      // redirect with the correct `to.fullPath`.
      const isInitialBoot = !routeName
      const safePath = isAuthRouteName(routeName) ? null : currentRoute.fullPath

      const { target, wasFirst } = await handleSessionExpired(safePath)
      if (wasFirst && !isInitialBoot && !isAuthRouteName(routeName)) {
        // Swallow NavigationDuplicated / NavigationAborted — meaningless here.
        // eslint-disable-next-line unicorn/prefer-await -- fire-and-forget navigation; nothing to await
        router.push(target).catch(() => {})
      }
    }
    throw error
  },
)

export default apiClient
